import { WarehouseBadge } from "@/components/OrderColumns";
import { RECALLED_HOLD } from "@logiful/domain";
import * as Dialog from "@radix-ui/react-dialog";
import { packUnitBreakdown } from "@logiful/domain";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CalendarClock, ArrowRight, Ban, Undo2, Check, ChevronDown, Repeat, RotateCcw, Split, History, MoreVertical, Send, TriangleAlert, CheckCircle2, Clock, Copy, ExternalLink, Gift, MapPin, Pencil, PackageCheck, PauseCircle, RefreshCw, StickyNote, Truck, Workflow, X, XCircle, Mail, Phone, Package, MapPinCheck, Globe2, Layers, Info } from "lucide-react";
import { useState, type ReactNode } from "react";
import { ADDRESS_AUDIT_ACTIONS, EU_COUNTRIES, RESHIP_REASONS, type ReshipReason } from "@logiful/domain";
import { msg, t, tn } from "@/lib/i18n";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import { api, ApiError, canManage, type OrderDetail, type PackageTracking } from "@/lib/api";
import { DELIVERY_VIEW } from "@/lib/delivery";
import { ManualOrderSheet } from "./ManualOrderSheet";
import { CONDITION_LABEL, ReturnsPortalButton, ShopifyReturnStatus } from "@/pages/Returns";
import { countryName, fmtDateTime, fmtMoney, fmtRelative, trackingUrl } from "@/lib/format";
import { useTenant } from "./AppShell";
import { Badge, Button, cx, Skeleton, Tip } from "./ui";
import { AddressRequestBox } from "@/components/AddressRequestBox";
import { Flag } from "@/components/Flag";
import { OrderExportDocumentSection } from "@/components/ExportDocument";
import { OrderReservationInfo } from "@/components/ReservationSettings";
import { AvailabilitySummary, entriesFor, LineAvailability, useOrderAvailability } from "@/components/LineAvailability";
import { LineShipment, ShipmentSummary } from "@/components/LineShipment";
import { OrderB2bSection } from "@/components/B2bInfo";
import { OrderFbaSection } from "@/components/FbaInfo";
import { serverText } from "@/lib/server-text";
import { intlLocale } from "@/lib/i18n";

/** kurze Beschriftungen, wenn nach dem Versand ein sechster Schritt folgt */
const SHORT: Record<string, string> = { "In Bearbeitung": "Picking", "Zur Abholung bereit": "Abholbereit", "Zurück an Absender": "Zurück", "Teilw. retourniert": "Teilretoure", "In Zustellung": "Zustellung" };
const STEPS = [
  { key: "received", label: msg("Eingang") },
  { key: "check", label: msg("Prüfung") },
  { key: "warehouse", label: msg("Übertragen") },
  { key: "picking", label: msg("In Bearbeitung") },
  { key: "shipped", label: msg("Versendet") },
] as const;

/**
 * Status → Fortschritt. Schritte vor `step` sind erledigt. Am Schritt `step` gilt:
 * running = läuft gerade (z. B. Kommissionierung), sonst wartet er noch; problem = zurückgehalten/abgelehnt.
 * Gleiche Logik in Liste und Detail: FFN-Stand Übertragen → In Bearbeitung (Picking) → Versendet.
 */
function progress(o: OrderDetail): { step: number; running: boolean; problem: "hold" | "rejected" | null; label?: string } {
  switch (o.status) {
    case "received": return { step: 1, running: true, problem: null };
    case "on_hold": return { step: 1, running: false, problem: "hold" };
    case "ready": return { step: 1, running: true, problem: null, label: o.releaseAt && new Date(o.releaseAt).getTime() > Date.now() ? "Stornofenster" : "Wird übertragen" };
    case "submitted": return { step: 3, running: false, problem: null };
    case "in_progress": return { step: 3, running: true, problem: null, label: msg("Picking läuft") };
    case "partially_shipped": return { step: 4, running: true, problem: null, label: msg("Teilversendet") };
    case "shipped": return { step: 5, running: false, problem: null };
    case "cancelled": return { step: 1, running: false, problem: "rejected" };
    case "failed": return { step: 2, running: false, problem: "rejected" };
    default: return { step: 0, running: false, problem: null };
  }
}

/** Name des Kanals für Hinweise („an Odoo gemeldet“ statt pauschal Shopify) */
const SHOP_LABEL: Record<string, string> = { shopify: "Shopify", odoo: "Odoo", woocommerce: "WooCommerce", shopware: "Shopware", kaufland: "Kaufland", amazon: "Amazon", sftp: "SFTP" };
const shopLabel = (kind?: string) => SHOP_LABEL[kind ?? ""] ?? "Shop";
const ACTOR = { user: "Portal", system: "Logiful", channel: "Shop", backend: "Lager" } as Record<string, string>;

export function OrderSheet({ orderId, onClose }: { orderId: string | null; onClose: () => void }) {
  const { tenantId } = useTenant();
  const q = useQuery({
    queryKey: ["order", tenantId, orderId],
    queryFn: () => api.orders.get(tenantId, orderId!),
    enabled: !!orderId,
    // live: während der Verarbeitung alle 2,5 s, sonst alle 10 s
    refetchInterval: (qq) => (qq.state.data && ["received", "ready"].includes(qq.state.data.status) ? 2500 : 10000),
  });
  return (
    <Dialog.Root open={!!orderId} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/20 animate-fade-in" />
        <Dialog.Content aria-describedby={undefined} onOpenAutoFocus={(e) => e.preventDefault()} data-devtools-host className="fixed z-50 right-0 top-0 h-full w-[min(620px,100vw)] bg-surface border-l border-border shadow-2xl flex flex-col animate-sheet-in outline-none">
          {q.data ? <Body o={q.data} /> : (
            <div className="p-6 space-y-4">
              <Dialog.Title className="sr-only">{t("Auftrag wird geladen")}</Dialog.Title>
              <Skeleton className="h-6 w-40" /><Skeleton className="h-10 w-full" /><Skeleton className="h-40 w-full" />
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Body({ o }: { o: OrderDetail }) {
  const { tenantId, me } = useTenant();
  const qc = useQueryClient();
  const p = progress(o);
  const a = o.shippingAddress;
  const blockedLines = o.lines.filter((l) => l.blocked || l.unknown);
  const [editing, setEditing] = useState(false);
  const [reshipping, setReshipping] = useState(false);
  const [partialOpen, setPartialOpen] = useState(false);
  const navigate = useNavigate();
  const [forcing, setForcing] = useState(false);
  const [deferring, setDeferring] = useState(false);
  /** Freigabetermin: bis zur Kommissionierung möglich (im Lager live geprüft) */
  // vor der Übergabe ans Lager: Verfügbarkeit je Position (reserviert / fehlt / wer den Bestand hält)
  const pendingStock = !o.importedFrom && o.channelKind !== "production" && ["received", "ready", "on_hold", "failed"].includes(o.status);
  const availability = useOrderAvailability(o.id, pendingStock);
  // übertragen, noch nicht kommissioniert: zurück ins Portal holen (Platz und Bestand bleiben)
  const canRecall = !o.importedFrom && o.channelKind !== "production" && o.status === "submitted";
  const recall = useMutation({
    mutationFn: (release: boolean) => api.orders.recall(tenantId, o.id, release),
    onSuccess: (_r, release) => {
      toast.success(release ? t("Freigegeben – geht wieder ans Lager") : t("Aus dem Lager zurückgeholt"), release ? undefined : { description: t("Platz und Bestand bleiben reserviert. Jetzt bearbeiten oder teilliefern – „Freigeben“ überträgt ihn wieder.") });
      void qc.invalidateQueries({ queryKey: ["order", tenantId, o.id] });
      void qc.invalidateQueries({ queryKey: ["orders-search"] });
      void qc.invalidateQueries({ queryKey: ["order-reservation", tenantId, o.id] });
    },
    onError: (e) => toast.error(t("Nicht möglich"), e instanceof ApiError ? { description: serverText((e.body as { message?: string })?.message ?? "") } : undefined),
  });
  const canDefer = !o.importedFrom && o.channelKind !== "production" && !["cancelled", "shipped", "partially_shipped", "in_progress"].includes(o.status);
  const { role } = useTenant();
  const manage = canManage(role);
  const cancel = useMutation({
    mutationFn: (mode?: "logiful" | "return_to_shop") => api.orders.cancel(tenantId, o.id, mode),
    onSuccess: (r, mode) => {
      toast.success(t("Auftrag {n} bei Logiful storniert", { n: o.orderNumber }), { description: [r.inWarehouse ? t("Auch im Lager storniert – live geprüft.") : null, mode === "return_to_shop" ? t("Wird in Shopify an den Shop zurückgegeben.") : mode === "logiful" ? t("Im Shop unverändert.") : null].filter(Boolean).join(" ") || undefined });
      void qc.invalidateQueries({ queryKey: ["order", tenantId, o.id] });
      void qc.invalidateQueries({ queryKey: ["orders-search", tenantId] });
    },
    onError: (e) => {
      toast.error("Storno nicht möglich", { description: e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Bitte erneut versuchen.") : "Bitte erneut versuchen.", duration: 10_000 });
      // Status kann sich durch die Live-Prüfung geändert haben (z. B. In Bearbeitung)
      void qc.invalidateQueries({ queryKey: ["order", tenantId, o.id] });
      void qc.invalidateQueries({ queryKey: ["orders-search", tenantId] });
    },
  });
  const imported = !!o.importedFrom;
  const rerequest = useMutation({
    mutationFn: () => api.orders.rerequest(tenantId, o.id),
    onSuccess: (r) => {
      toast.success(t("Auftrag {n} erneut bei Logiful angefragt", { n: o.orderNumber }), { description: t("Läuft wieder durch alle Prüfungen – fehlt Bestand, geht er nach dem Wareneingang automatisch ans Lager. Im Lager als {w}.", { w: r.warehouseOrderNumber }) });
      void qc.invalidateQueries({ queryKey: ["order", tenantId, o.id] });
      void qc.invalidateQueries({ queryKey: ["orders-search", tenantId] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? t("Fehler")) : t("Fehler")),
  });
  const canReprocess = !imported && (o.status === "on_hold" || o.status === "ready" || o.status === "failed");
  /** versendet (auch teilweise) → Nachlieferung als Schnellaktion; sonst im Menü */
  const shippedish = ["shipped", "partially_shipped"].includes(o.status);

  const reprocess = useMutation({
    mutationFn: () => api.orders.reprocess(tenantId, o.id),
    onSuccess: () => {
      toast.success("Auftrag wird erneut geprüft", { description: msg("Ist alles in Ordnung, geht er direkt ans Lager.") });
      void qc.invalidateQueries({ queryKey: ["order", tenantId, o.id] });
      void qc.invalidateQueries({ queryKey: ["orders-search", tenantId] });
    },
  });

  // Fehlbestand: Übertragung bis zu Logiful erzwingen bzw. im FFN geparkten Auftrag übergeben
  const backorder = useMutation({
    mutationFn: () => api.orders.backorderRelease(tenantId, o.id),
    onSuccess: (r) => {
      toast.success(r.handOver ? "Wird an Logiful übergeben" : "Wird trotz Fehlbestand übertragen", { description: msg("Logiful versendet, sobald die Ware eingebucht ist.") });
      void qc.invalidateQueries({ queryKey: ["order", tenantId, o.id] });
      void qc.invalidateQueries({ queryKey: ["orders-search", tenantId] });
    },
    onError: (e) => toast.error("Nicht möglich", e instanceof ApiError ? { description: (e.body as { message?: string })?.message } : undefined),
  });
  const parked = o.status === "submitted" && !!o.warehouseParkedAt;

  // Lager-Versandart mit Namen statt ID (aus den Versandarten der Lager)
  const methods = useQuery({ queryKey: ["shipping-methods", tenantId], queryFn: () => api.backend.shippingMethods(tenantId), staleTime: 10 * 60_000 });
  const methodName = (id: string | null) => {
    if (!id) return "Standard des Lagers";
    for (const w of methods.data?.warehouses ?? []) {
      const m = w.shippingMethods.find((x) => x.id === id);
      if (m) return m.label || m.name;
    }
    return id;
  };
  /** interner Lagername (nur Logiful) – wenn er von der Kundenbezeichnung abweicht */
  const internalMethodName = (id: string | null) => {
    for (const w of methods.data?.warehouses ?? []) {
      const m = w.shippingMethods.find((x) => x.id === id);
      if (m && m.label && m.label !== m.name) return m.name;
    }
    return null;
  };

  // neu aus dem Kanal abrufen: nur solange der Auftrag nicht beim Lager ist
  const canRefetch = !o.importedFrom && ["received", "ready", "on_hold", "failed"].includes(o.status) && !["manual", "b2b", "csv", "fba", "sftp"].includes(o.channelKind ?? "");
  const refetch = useMutation({
    mutationFn: () => api.orders.refetch(tenantId, o.id),
    onSuccess: (r) => {
      toast.success(r.queued ? `Wird neu aus ${shopLabel(o.channelKind)} abgerufen` : r.changes.length ? `Aktualisiert: ${r.changes.join(", ")}` : "Keine Änderungen im Kanal", { description: r.queued ? "Das Ergebnis steht gleich im Verlauf." : r.addressKept ? "Lieferadresse aus der Adressklärung beibehalten." : "Der Auftrag wird neu geprüft." });
      setTimeout(() => void qc.invalidateQueries({ queryKey: ["order", tenantId, o.id] }), r.queued ? 4000 : 1500);
    },
    onError: (e) => toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Nicht möglich") : "Nicht möglich"),
  });
  // Nur Logiful: geprüfte Retoure aller Positionen anlegen und wie eine echte an Shopify melden
  const canTestReturn = me.user.staffRole === "staff_admin" && o.channelKind === "shopify" && shippedish;
  const testReturn = useMutation({
    mutationFn: () => api.admin.testReturn(tenantId, o.id),
    onSuccess: () => {
      toast.success("Testretoure angelegt", { description: msg("Wird jetzt an Shopify gemeldet – das Ergebnis erscheint hier am Auftrag.") });
      void qc.invalidateQueries({ queryKey: ["order-returns", tenantId, o.id] });
    },
    onError: (e) => toast.error("Testretoure nicht angelegt", { description: e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "") : "" }),
  });

  const copy = (text: string, label: string) => void navigator.clipboard?.writeText(text).then(() => toast.success(`${label} kopiert`));

  return (
    <>
      {/* Kopf */}
      <div className="px-6 pt-5 pb-4 border-b border-border">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <Dialog.Title className="text-[18px] font-semibold tabular-nums flex items-center gap-2">
              {t("Auftrag")} {o.orderNumber}
              <button onClick={() => copy(o.orderNumber, "Auftragsnummer")} className="text-fg-subtle hover:text-fg" aria-label={t("Auftragsnummer kopieren")}><Copy className="size-3.5" /></button>
              {o.addressCorrection && <Badge tone="info" icon={<MapPinCheck className="size-3" />}>{t("Adresse korrigiert")}</Badge>}
            </Dialog.Title>
            <p className="text-[12.5px] text-fg-muted mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
              <span>{o.channelName} {t("· bestellt")} {fmtRelative(o.placedAt)} · {fmtMoney(o.totalMinor, o.currency)}</span>
              {o.links.shop && <a href={o.links.shop} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-fg-muted hover:text-fg underline-offset-2 hover:underline"><ExternalLink className="size-3" /> {t("In Shopify öffnen")}</a>}
            </p>
            {o.testMode && <p className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] text-fg-muted"><Badge tone="warning">{t("Testauftrag")}</Badge> {t("im Testbetrieb geprüft – geht nie ans Lager, keine Aktion im Shop")}</p>}
            {imported && <p className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] text-fg-muted"><Badge icon={<History className="size-3" />}>{t("Historie aus dem Lager")}</Badge> {t("nur Ansicht – keine Aktionen im Shop oder Lager")}</p>}
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {o.editable && <Button size="sm" icon={<Pencil className="size-3.5" />} onClick={() => setEditing(true)}><span className="hidden sm:inline">{t("Bearbeiten")}</span></Button>}
            {shippedish && <Button size="sm" icon={<Repeat className="size-3.5" />} onClick={() => setReshipping(true)}><span className="hidden sm:inline">{t("Nachlieferung")}</span></Button>}
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button className="size-8 grid place-items-center rounded-lg border border-border hover:bg-surface-2 text-fg-muted data-[state=open]:bg-surface-2" aria-label={t("Weitere Aktionen")}><MoreVertical className="size-4" /></button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content align="end" sideOffset={6} className="z-[60] min-w-[240px] rounded-xl border border-border bg-surface p-1 shadow-xl animate-fade-in">
                  {canReprocess && (
                    <DropdownMenu.Item onSelect={() => reprocess.mutate()} className="flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-[13px] outline-none cursor-pointer data-[highlighted]:bg-surface-2">
                      <RefreshCw className="size-4 mt-0.5 text-fg-muted" />
                      <span><span className="block font-medium">{t("Erneut prüfen")}</span><span className="block text-[11.5px] text-fg-subtle">{t("Regeln und Daten neu prüfen, bei Erfolg direkt ans Lager")}</span></span>
                    </DropdownMenu.Item>
                  )}
                  {o.editable && manage && (
                    <DropdownMenu.Item onSelect={() => setForcing(true)} className="flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-[13px] outline-none cursor-pointer data-[highlighted]:bg-surface-2">
                      <Send className="size-4 mt-0.5 text-fg-muted" />
                      <span><span className="block font-medium">{t("Übertragung erzwingen")}</span><span className="block text-[11.5px] text-fg-subtle">{t("Zurückhaltung übergehen und ans Lager senden")}</span></span>
                    </DropdownMenu.Item>
                  )}
                  {!shippedish && o.status !== "cancelled" && (
                    <DropdownMenu.Item onSelect={() => setReshipping(true)} className="flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-[13px] outline-none cursor-pointer data-[highlighted]:bg-surface-2">
                      <Repeat className="size-4 mt-0.5 text-fg-muted" />
                      <span><span className="block font-medium">{t("Nachlieferung anlegen")}</span><span className="block text-[11.5px] text-fg-subtle">{t("Kopie als neuer Auftrag, alles änderbar")}</span></span>
                    </DropdownMenu.Item>
                  )}
                  {canRecall && (
                    <DropdownMenu.Item
                      onSelect={() => {
                        if (confirm(t("Auftrag {n} aus dem Lager zurückholen?\n\nDas Lager storniert ihn (live geprüft, nur solange er noch nicht kommissioniert wird). Er bleibt dann im Portal zurückgehalten – mit seinem Platz in der Bestandsvergabe und seinem Bestand –, bis du ihn freigibst. So kannst du ihn bearbeiten oder teilliefern, ohne dass der Bestand an andere Aufträge geht.", { n: o.orderNumber }))) recall.mutate(false);
                      }}
                      className="flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-[13px] outline-none cursor-pointer data-[highlighted]:bg-surface-2"
                    >
                      <Undo2 className="size-4 mt-0.5 text-fg-muted" />
                      <span><span className="block font-medium">{t("Aus dem Lager zurückholen")}</span><span className="block text-[11.5px] text-fg-subtle">{t("zum Bearbeiten oder Teilliefern – Platz und Bestand bleiben")}</span></span>
                    </DropdownMenu.Item>
                  )}
                  {canDefer && (
                    <DropdownMenu.Item onSelect={() => setDeferring(true)} className="flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-[13px] outline-none cursor-pointer data-[highlighted]:bg-surface-2">
                      <CalendarClock className="size-4 mt-0.5 text-fg-muted" />
                      <span><span className="block font-medium">{o.deferredUntil ? t("Freigabetermin ändern") : t("Freigabetermin setzen")}</span><span className="block text-[11.5px] text-fg-subtle">{t("erst ab einem Datum versenden, z. B. Endkunde im Urlaub")}</span></span>
                    </DropdownMenu.Item>
                  )}
                  {canRefetch && (
                    <DropdownMenu.Item onSelect={() => refetch.mutate()} className="flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-[13px] outline-none cursor-pointer data-[highlighted]:bg-surface-2">
                      <RefreshCw className="size-4 mt-0.5 text-fg-muted" />
                      <span><span className="block font-medium">{t("Neu aus")} {shopLabel(o.channelKind)} {t("abrufen")}</span><span className="block text-[11.5px] text-fg-subtle">{t("Adresse, Positionen, Chargen, Hinweise aktualisieren")}</span></span>
                    </DropdownMenu.Item>
                  )}
                  <DropdownMenu.Item onSelect={() => copy(o.orderNumber, "Auftragsnummer")} className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] outline-none cursor-pointer data-[highlighted]:bg-surface-2"><Copy className="size-4 text-fg-muted" /> {t("Auftragsnummer kopieren")}</DropdownMenu.Item>
                  {o.links.backendRef && <DropdownMenu.Item onSelect={() => copy(o.links.backendRef!, "Lager-Referenz")} className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] outline-none cursor-pointer data-[highlighted]:bg-surface-2"><Copy className="size-4 text-fg-muted" /> {t("Lager-Referenz kopieren")}</DropdownMenu.Item>}
                  {canTestReturn && (
                  <>
                    <DropdownMenu.Separator className="my-1 h-px bg-border" />
                    <DropdownMenu.Item onSelect={() => { if (confirm("Testretoure aller Positionen anlegen und an Shopify melden? In Shopify entsteht eine echte Retoure.")) testReturn.mutate(); }} className="flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-[13px] outline-none cursor-pointer data-[highlighted]:bg-surface-2">
                      <RotateCcw className="size-4 mt-0.5 text-fg-muted" />
                      <span><span className="block font-medium">{t("Testretoure an Shopify melden")}</span><span className="block text-[11.5px] text-fg-subtle">{t("Nur Logiful – legt eine echte Retoure in Shopify an")}</span></span>
                    </DropdownMenu.Item>
                  </>
                )}
                {o.channelKind === "manual" && !imported && !["cancelled", "shipped", "partially_shipped", "in_progress"].includes(o.status) && (
                    <>
                      <DropdownMenu.Separator className="my-1 h-px bg-border" />
                      <DropdownMenu.Item onSelect={() => { if (confirm(o.editable ? `Auftrag ${o.orderNumber} wirklich stornieren?` : `Auftrag ${o.orderNumber} ist schon übertragen. Das Portal fragt jetzt live beim Lager nach und storniert dort, sofern die Kommissionierung noch nicht begonnen hat. Fortfahren?`)) cancel.mutate(); }} className="flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-[13px] text-danger outline-none cursor-pointer data-[highlighted]:bg-danger-soft">
                        <Ban className="size-4 mt-0.5" />
                        <span><span className="block font-medium">{t("Auftrag stornieren")}</span>{!o.editable && <span className="block text-[11.5px] opacity-80">{t("prüft live im Lager")}</span>}</span>
                      </DropdownMenu.Item>
                    </>
                  )}
                  {o.status === "cancelled" && !imported && o.channelKind !== "production" && (
                    <>
                      <DropdownMenu.Separator className="my-1 h-px bg-border" />
                      <DropdownMenu.Item onSelect={() => rerequest.mutate()} className="flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-[13px] outline-none cursor-pointer data-[highlighted]:bg-surface-2">
                        <Send className="size-4 mt-0.5 text-fg-muted" />
                        <span><span className="block font-medium">{t("Fulfillment erneut bei Logiful anfragen")}</span><span className="block text-[11.5px] text-fg-subtle">{t("derselbe Auftrag – bei Fehlbestand automatisch nach Wareneingang")}</span></span>
                      </DropdownMenu.Item>
                    </>
                  )}
                  {o.channelKind === "shopify" && !imported && !["cancelled", "shipped", "partially_shipped"].includes(o.status) && (
                    <>
                      <DropdownMenu.Separator className="my-1 h-px bg-border" />
                      <DropdownMenu.Item onSelect={() => { if (confirm(t("Auftrag {n} nur bei Logiful stornieren? Lager und Connect stornieren (live geprüft, nur bis zur Kommissionierung) – im Shop bleibt die Bestellung unverändert.", { n: o.orderNumber }))) cancel.mutate("logiful"); }} className="flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-[13px] text-danger outline-none cursor-pointer data-[highlighted]:bg-danger-soft">
                        <Ban className="size-4 mt-0.5" />
                        <span><span className="block font-medium">{t("Bei Logiful stornieren")}</span><span className="block text-[11.5px] opacity-80">{t("Shop bleibt unverändert")}</span></span>
                      </DropdownMenu.Item>
                      <DropdownMenu.Item onSelect={() => { if (confirm(t("Auftrag {n} bei Logiful stornieren und in Shopify an den Shop zurückgeben? Shopify zeigt ihn dann als nicht ausgeführt – ihr könnt selbst versenden oder erneut anfordern.", { n: o.orderNumber }))) cancel.mutate("return_to_shop"); }} className="flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-[13px] text-danger outline-none cursor-pointer data-[highlighted]:bg-danger-soft">
                        <Undo2 className="size-4 mt-0.5" />
                        <span><span className="block font-medium">{t("Bei Logiful stornieren und an Shop zurückgeben")}</span><span className="block text-[11.5px] opacity-80">{t("in Shopify „nicht ausgeführt“")}</span></span>
                      </DropdownMenu.Item>
                      <div className="px-2.5 py-2 text-[11.5px] text-fg-subtle">{t("Die Bestellung ganz stornieren (inkl. Erstattung) bitte in Shopify – das Portal prüft dann live im Lager.")}</div>
                    </>
                  )}
                  {o.channelKind !== "manual" && o.channelKind !== "shopify" && o.channelKind !== "production" && !imported && !["cancelled", "shipped", "partially_shipped", "in_progress"].includes(o.status) && (
                    <>
                      <DropdownMenu.Separator className="my-1 h-px bg-border" />
                      <DropdownMenu.Item onSelect={() => { if (confirm(t("Auftrag {n} bei Logiful stornieren? Lager und Connect stornieren (live geprüft, nur bis zur Kommissionierung) – in {k} bleibt die Bestellung unverändert.", { n: o.orderNumber, k: shopLabel(o.channelKind) }))) cancel.mutate(undefined); }} className="flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-[13px] text-danger outline-none cursor-pointer data-[highlighted]:bg-danger-soft">
                        <Ban className="size-4 mt-0.5" />
                        <span><span className="block font-medium">{t("Bei Logiful stornieren")}</span><span className="block text-[11.5px] opacity-80">{shopLabel(o.channelKind)} {t("bleibt unverändert")}</span></span>
                      </DropdownMenu.Item>
                      <div className="px-2.5 py-2 text-[11.5px] text-fg-subtle">{t("Die Bestellung ganz stornieren (inkl. Erstattung) bitte in")} {shopLabel(o.channelKind)} {t("– das Portal prüft dann live im Lager.")}</div>
                    </>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
            <Dialog.Close asChild><button className="size-8 grid place-items-center rounded-lg hover:bg-surface-2 text-fg-muted" aria-label={t("Schließen")}><X className="size-4" /></button></Dialog.Close>
          </div>
        </div>
        <ForceDialog o={o} open={forcing} onOpenChange={setForcing} />
        {deferring && <DeferDialog o={o} tenantId={tenantId} onClose={() => setDeferring(false)} />}
        {o.deferredUntil && o.status === "on_hold" && (
          <div className="mt-3 rounded-lg border border-border bg-surface-2/50 px-3 py-2 text-[12.5px] flex flex-wrap items-center gap-x-2 gap-y-1">
            <CalendarClock className="size-4 text-fg-muted" />
            <span>{t("Zurückgestellt bis {d} – geht dann automatisch ans Lager. Bestand bleibt reserviert.", { d: new Date(o.deferredUntil).toLocaleDateString(intlLocale()) })}</span>
            {canDefer && <button type="button" onClick={() => setDeferring(true)} className="ml-auto text-[12.5px] font-medium underline underline-offset-2">{t("Ändern")}</button>}
          </div>
        )}
        {partialOpen && <PartialDialog o={o} onClose={() => setPartialOpen(false)} />}
        <OrderReturns orderId={o.id} orderNumber={o.orderNumber} canTest={o.channelKind === "shopify" && (o.status === "shipped" || o.status === "partially_shipped")} />
        {o.replacedBy && (
          <div className="mt-3 rounded-lg border border-info/30 bg-info-soft px-3 py-2 text-[12.5px] flex flex-wrap items-center gap-x-2 gap-y-1">
            <Repeat className="size-3.5 text-info" />
            <span>{t("In {shop} neu angefordert – geht weiter als", { shop: shopLabel(o.channelKind) })} <Link to={`/orders?open=${o.replacedBy.id}`} className="font-medium underline underline-offset-2">{o.replacedBy.orderNumber}</Link></span>
          </div>
        )}
        {(o.shopRequests?.length ?? 0) > 1 && (
          <div className="mt-3 rounded-lg border border-border bg-surface-2/50 px-3 py-2 text-[12.5px]">
            <div className="flex items-center gap-1.5 text-fg-muted">
              <Layers className="size-3.5 text-fg-subtle" />
              {t("{n} Fulfillment-Anfragen zu dieser Bestellung in {shop}", { n: o.shopRequests!.length, shop: shopLabel(o.channelKind) })}
              <Tip content={t("Je Fulfillment-Anfrage legt das Portal einen Auftrag an – z. B. wenn im Shop der Standort geändert, die Anfrage storniert und neu gestellt oder Positionen zusammengelegt wurden.")}><span><Info className="size-3.5 text-fg-subtle" /></span></Tip>
            </div>
            <ul className="mt-1.5 space-y-1">
              {o.shopRequests!.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-x-2">
                  {r.id === o.id ? <b className="tabular-nums">{r.orderNumber}</b> : <Link to={`/orders?open=${r.id}`} className="font-medium tabular-nums underline underline-offset-2">{r.orderNumber}</Link>}
                  <span className={r.status === "cancelled" ? "text-fg-subtle" : "text-fg-muted"}>{r.replaced ? t("ersetzt") : (PART_STATUS[r.status] ?? r.status)} · {t("{n} Stk.", { n: r.itemCount })} · {new Date(r.createdAt).toLocaleString(intlLocale(), { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                  {r.id === o.id && <span className="text-[11px] text-fg-subtle">{t("(dieser Auftrag)")}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {(o.partialParts?.length ?? 0) > 1 && (
          <div className="mt-3 rounded-lg border border-border bg-surface-2/50 px-3 py-2 text-[12.5px] flex flex-wrap items-center gap-x-2 gap-y-1">
            <Split className="size-3.5 text-fg-subtle" /> <span className="text-fg-muted">{t("Teillieferung:")}</span>
            {o.partialParts!.map((p, i) => (
              <span key={p.id}>
                {i > 0 && <span className="text-fg-subtle mr-2">·</span>}
                {p.id === o.id ? <b>{p.orderNumber}</b> : <Link to={`/orders?open=${p.id}`} className="font-medium underline underline-offset-2">{p.orderNumber}</Link>}
                <span className="text-fg-muted"> ({PART_STATUS[p.status] ?? p.status})</span>
              </span>
            ))}
          </div>
        )}
        {editing && <ManualOrderSheet open edit={o} onOpenChange={(v) => !v && setEditing(false)} onCreated={() => undefined} />}
        {reshipping && <ManualOrderSheet open reshipOf={o} onOpenChange={(v) => !v && setReshipping(false)} onCreated={(id) => navigate(`/orders?open=${id}`)} />}
        {(o.reshipOf || (o.reships?.length ?? 0) > 0) && (
          <div className="mt-3 rounded-lg border border-border bg-surface-2/50 px-3 py-2 text-[12.5px] flex flex-wrap items-center gap-x-2 gap-y-1">
            <Repeat className="size-3.5 text-fg-subtle" />
            {o.reshipOf && <span>{t("Nachlieferung zu")} <Link to={`/orders?open=${o.reshipOf.id}`} className="font-medium underline underline-offset-2">{o.reshipOf.orderNumber}</Link>{o.reshipOf.reason && <span className="text-fg-muted"> · {RESHIP_REASONS[o.reshipOf.reason as ReshipReason] ?? o.reshipOf.reason}</span>}</span>}
            {(o.reships?.length ?? 0) > 0 && <span>{t("Nachlieferung")}{o.reships!.length > 1 ? t("en") : ""}: {o.reships!.map((r, i) => <span key={r.id}>{i > 0 && ", "}<Link to={`/orders?open=${r.id}`} className="font-medium underline underline-offset-2">{r.orderNumber}</Link>{r.reason && <span className="text-fg-muted"> ({RESHIP_REASONS[r.reason as ReshipReason] ?? r.reason})</span>}</span>)}</span>}
          </div>
        )}

        {/* Fortschritt – nach dem Versand ggf. ein sechster Schritt: wo das Paket ist bzw. Retoure */}
        {(() => {
          const shippedNow = o.status === "shipped" || o.status === "partially_shipped";
          const after = !shippedNow
            ? null
            : o.returnState
              ? o.returnState === "undeliverable"
                ? { label: msg("Rückläufer"), bar: "bg-danger", text: msg("text-danger"), icon: <TriangleAlert className="size-3 shrink-0 text-danger" /> }
                : { label: o.returnState === "returned" ? "Retourniert" : "Teilw. retourniert", bar: "bg-fg-subtle/60", text: msg("text-fg"), icon: <RotateCcw className="size-3 shrink-0 text-fg-muted" /> }
              : o.deliveryStatus && o.deliveryStatus !== "pre_transit"
                ? (() => {
                    const v = DELIVERY_VIEW[o.deliveryStatus];
                    return v.tone === "danger"
                      ? { label: v.label, bar: "bg-danger", text: msg("text-danger"), icon: <TriangleAlert className="size-3 shrink-0 text-danger" /> }
                      : v.tone === "warning"
                        ? { label: v.label, bar: "bg-warning", text: msg("text-warning"), icon: <Clock className="size-3 shrink-0 text-warning" /> }
                        : o.deliveryStatus === "delivered"
                          ? { label: v.label, bar: "bg-success", text: msg("text-fg"), icon: <Check className="size-3 shrink-0 text-success" /> }
                          : { label: v.label, bar: "bg-success/50", text: msg("text-fg"), icon: <Truck className="size-3 shrink-0 text-fg-muted" /> };
                  })()
                : null;
          return (
            <ol className={cx("mt-5 grid gap-1", after ? "grid-cols-[repeat(5,minmax(0,1fr))_minmax(0,1.3fr)]" : "grid-cols-5")} aria-label={t("Fortschritt")}>
              {STEPS.map((s, i) => {
                // erledigt (grün) · läuft (grau, fett) · wartet (leer) · Problem (orange/rot)
                const done = i < p.step;
                const problemHere = !!p.problem && i === p.step;
                // storniert (Kunde/Shop/Logiful) ist kein Fehler – „abgelehnt“ nur, wenn die Auftragsregeln abgelehnt haben
                const rejected = o.status === "cancelled" && !!o.history?.some((h) => h.action === "order.rejected");
                const cancelledOnly = o.status === "cancelled" && !rejected;
                const current = !p.problem && i === p.step && p.running;
                return (
                  <li key={s.key} className="min-w-0">
                    {/* laufender Schritt: halb gefüllt (grün) – erledigt bis zur Hälfte, Rest folgt */}
                    <div className={cx("h-1.5 rounded-full overflow-hidden", done ? "bg-success" : problemHere ? (p.problem === "hold" ? "bg-warning" : cancelledOnly ? "bg-fg-subtle/50" : "bg-danger") : "bg-surface-2")}>
                      {current && <div className="h-full w-1/2 rounded-full bg-success" />}
                    </div>
                    <p className={cx("mt-1.5 flex items-center gap-1 truncate", after ? "text-[11px]" : "text-[11.5px]", done || current ? "text-fg font-medium" : "text-fg-subtle")}>
                      {done && !after && <Check className="size-3 shrink-0 text-success" />}
                      {problemHere && (p.problem === "hold" ? <PauseCircle className="size-3 text-warning" /> : cancelledOnly ? <Ban className="size-3 text-fg-muted" /> : <XCircle className="size-3 text-danger" />)}
                      {problemHere ? (p.problem === "hold" ? t("Zurückgehalten") : rejected ? t("Abgelehnt") : cancelledOnly ? (o.replacedBy ? t("Ersetzt") : t("Storniert")) : t("Fehler")) : current && p.label ? p.label : after ? (SHORT[s.label] ?? s.label) : s.label}
                    </p>
                  </li>
                );
              })}
              {after && (
                <li className="min-w-0">
                  <div className={cx("h-1.5 rounded-full", after.bar)} />
                  <p className={cx("mt-1.5 text-[11px] flex items-center gap-1 truncate font-medium", after.text)} title={t(after.label)}>{after.icon}{SHORT[after.label] ?? after.label}</p>
                </li>
              )}
            </ol>
          );
        })()}
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
        {o.status === "in_progress" && (
          <section className="rounded-xl border border-info/25 bg-info-soft/50 p-3.5 text-[13px]">
            <p className="font-medium">{t("Das Lager kommissioniert diesen Auftrag gerade.")}</p>
            <p className="text-fg-muted mt-0.5">{t("Ein Storno ist ab jetzt nicht mehr automatisch möglich – bitte direkt bei Logiful melden.")}</p>
          </section>
        )}
        {parked && (
          <section className="rounded-xl border border-info/25 bg-info-soft/50 p-3.5 text-[13px]">
            <p className="font-medium">{t("Im FFN angelegt, wartet auf Bestand – noch nicht bei Logiful.")}</p>
            <p className="text-fg-muted mt-0.5">{t("Das Portal übergibt den Auftrag automatisch an Logiful, sobald der Bestand reicht.")}</p>
            {!imported && <Button className="mt-2.5" size="sm" variant="primary" icon={<Send className="size-3.5" />} loading={backorder.isPending} onClick={() => backorder.mutate()}>{t("Jetzt an Logiful übergeben")}</Button>}
          </section>
        )}
        {o.status === "failed" && (
          <section className="rounded-xl border border-danger/25 bg-danger-soft/50 p-4 text-[13px]">
            <p className="font-semibold flex items-center gap-2"><XCircle className="size-4 text-danger" /> {t("Übergabe ans Lager fehlgeschlagen")}</p>
            {o.holdReasons.length > 0 && <ul className="mt-1.5 space-y-1 text-fg-muted">{o.holdReasons.map((r) => <li key={r}>{serverText(r)}</li>)}</ul>}
            {canReprocess && <Button className="mt-2.5" size="sm" variant="primary" icon={<RefreshCw className="size-3.5" />} loading={reprocess.isPending} onClick={() => reprocess.mutate()}>{t("Erneut prüfen")}</Button>}
          </section>
        )}
        {o.b2b && <OrderB2bSection orderId={o.id} b2b={o.b2b} />}
        {o.fba && <OrderFbaSection order={o} fba={o.fba} />}
        <OrderExportDocumentSection orderId={o.id} waiting={o.status === "on_hold" && o.holdReasons.some((r) => r.startsWith("Ausfuhrbegleitdokument"))} />
        {/* Sperre mit Handlungsanweisung */}
        {o.status === "on_hold" && (
          <section className="rounded-xl border border-warning/30 bg-warning-soft/60 p-4">
            <p className="font-semibold text-[13.5px] flex items-center gap-2"><PauseCircle className="size-4 text-warning" /> {t("Warum wird der Auftrag zurückgehalten?")}</p>
            <ul className="mt-2 space-y-1.5 text-[13px]">
              {o.holdReasons.map((r) => (
                <li key={r} className="flex items-start gap-2">
                  <span className="mt-1.5 size-1.5 rounded-full bg-warning shrink-0" />
                  <span>
                    {serverText(r)}
                    {r.startsWith("Zahlung") && <span className="block text-[12px] text-fg-muted">{t("Wird automatisch freigegeben, sobald die Zahlung in {shop} eingeht.", { shop: shopLabel(o.channelKind) })}</span>}
                    {r.startsWith("Fehlbestand") && (imported ? null : <OrderReservationInfo orderId={o.id} />)}
                    {r === RECALLED_HOLD && (
                      <span className="mt-1.5 flex flex-wrap items-center gap-2">
                        <Button size="sm" variant="primary" icon={<Send className="size-3.5" />} loading={recall.isPending} onClick={() => recall.mutate(true)}>{t("Freigeben")}</Button>
                        <span className="text-[12px] text-fg-muted">{t("Bis dahin bleibt der Bestand für diesen Auftrag reserviert.")}</span>
                      </span>
                    )}
                    {r.startsWith("Händler-Vorgabe") && <span className="block text-[12px] text-fg-muted">{t("Geht automatisch weiter, sobald passender Bestand im Lager ist. Sonst Menge unter „Bearbeiten“ anpassen oder mit dem Händler klären und über ⋮ → „Übertragung erzwingen“ trotzdem senden.")}</span>}
                    {(r.startsWith("Lieferadresse") || r.startsWith("Adresse prüfen: ")) && (
                      <span className="block text-[12px] text-fg-muted">
                        {r.includes("Postnummer")
                          ? t("Die Postnummer (DHL-Kundennummer) kennt nur der Empfänger – beim Kunden erfragen und unter „Bearbeiten“ im Adresszusatz eintragen. Der Auftrag wird danach automatisch neu geprüft.")
                          : r.includes("Hausnummer fehlt") || r.includes("Packstation-Nummer fehlt")
                            ? t("Unter „Bearbeiten“ ergänzen – fehlt die Angabe auch in der Bestellung, beim Kunden erfragen. Der Auftrag wird danach automatisch neu geprüft.")
                            : t("Über „Bearbeiten“ korrigieren – der Auftrag wird danach automatisch neu geprüft.")}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            {blockedLines.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {blockedLines.filter((l) => l.productId).map((l) => (
                  <Link key={l.id} to={`/products?open=${l.productId}`}><Button size="sm">{l.sku} {t("ergänzen")} <ArrowRight className="size-3.5" /></Button></Link>
                ))}
                <span className="text-[12px] text-fg-muted self-center">{t("Nach dem Speichern wird der Auftrag automatisch freigegeben.")}</span>
              </div>
            )}
            <AddressRequestBox order={o} />
            {!imported && (o.shortage || canReprocess) && (
              <div className="mt-3 pt-3 border-t border-warning/25 flex flex-wrap items-center gap-2">
                {o.partialPossible && <Button size="sm" variant="primary" icon={<Split className="size-3.5" />} onClick={() => setPartialOpen(true)}>{t("Teilliefern")}</Button>}
                {o.shortage && <Button size="sm" variant={o.partialPossible ? "secondary" : "primary"} icon={<Send className="size-3.5" />} loading={backorder.isPending} onClick={() => backorder.mutate()}>{t("Trotz Fehlbestand übertragen")}</Button>}
                {canReprocess && <Button size="sm" variant={o.shortage ? "ghost" : "primary"} icon={<RefreshCw className="size-3.5" />} loading={reprocess.isPending} onClick={() => reprocess.mutate()}>{t("Erneut prüfen")}</Button>}
              </div>
            )}
            {o.appliedRules.length > 0 && (
              <p className="mt-3 text-[12px] text-fg-muted flex items-center gap-1.5"><Workflow className="size-3.5" /> {t("Ausgelöst durch Regel:")} {o.appliedRules.map((r) => r.name).join(", ")} · <Link to="/rules" className="underline underline-offset-2">{t("Regeln ansehen")}</Link></p>
            )}
          </section>
        )}
        {o.status === "ready" && o.releaseAt && (
          <section className="rounded-xl border border-border bg-surface-2/60 p-4 text-[13px] flex items-center gap-2">
            {new Date(o.releaseAt).getTime() > Date.now() ? (
              <><Clock className="size-4 text-fg-muted" /> {t("Stornofenster läuft – geht {when} automatisch ans Lager.", { when: fmtRelative(o.releaseAt) })}</>
            ) : (
              <><RefreshCw className="size-4 text-fg-muted animate-spin" /> {t("Wird gerade ans Lager übertragen …")}</>
            )}
          </section>
        )}

        {/* Sendungen */}
        {o.shipments.length > 0 && (
          <Section title={t("Sendungen")} icon={<Truck className="size-4" />}>
            {o.shipments.map((s) => (
              <ShipmentCard key={s.id} s={s} o={o} copy={copy} />
            ))}
          </Section>
        )}

        {/* Positionen: kompakte Tabelle mit Bild, Menge, Preisen – vor der Übergabe ans Lager mit Verfügbarkeit je Position */}
        <Section title={t("Positionen · {v} Stk.", { v: o.lines.reduce((n, l) => n + l.quantity, 0) })} aside={pendingStock ? <AvailabilitySummary r={availability.data} lineProductIds={o.lines.filter((l) => !l.addedByRule).map((l) => l.productId)} /> : <ShipmentSummary o={o} />}>
          <div className="rounded-xl border border-border overflow-hidden">
            <table className="w-full table-fixed text-[13px]">
              <thead className="bg-surface-2/60 text-[11.5px] text-fg-muted">
                <tr>
                  <th className="text-left font-medium px-3 py-2">{t("Artikel")}</th>
                  <th className="text-right font-medium px-3 py-2 w-16">{t("Menge")}</th>
                  <th className="text-right font-medium px-3 py-2 w-24 hidden sm:table-cell">{t("Einzelpreis")}</th>
                  <th className="text-right font-medium px-3 py-2 w-24">{t("Summe")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {o.lines.map((l) => (
                  <tr key={l.id} className="align-middle">
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-3 min-w-0">
                        <LineThumb url={l.imageUrl ?? null} />
                        <div className="min-w-0">
                          {l.productId ? <Link to={`/products?open=${l.productId}`} className="font-medium hover:underline truncate block">{l.name}</Link> : <span className="font-medium truncate block">{l.name}</span>}
                          <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                            {o.channelKind === "shopify" && /^\d{10,}$/.test(l.sku) ? (
                              <Tip content={t("SKU aus der Shopify-Variante – in Shopify war keine SKU gepflegt, das Portal hat sie vergeben und dort ergänzt")}>
                                <span className="text-[12px] text-fg-muted tabular-nums cursor-help">{l.sku}</span>
                              </Tip>
                            ) : (
                              <span className="text-[12px] text-fg-muted tabular-nums">{l.sku}</span>
                            )}
                            {l.addedByRule && <Tip content={t("Automatisch hinzugefügt durch Regel „{addedByRule}“", { addedByRule: l.addedByRule })}><span><Badge icon={<Gift className="size-3" />}>{t("Beilage")}</Badge></span></Tip>}
                            {l.unknown && <Badge tone="danger">{t("unbekannt")}</Badge>}
                            {l.blocked && <Tip content={t("Stammdaten unvollständig – Artikel kann nicht ans Lager")}><span><Badge tone="danger">{t("Stammdaten")}</Badge></span></Tip>}
                          </div>
                          {packUnitBreakdown(l.quantity, l.packUnits) && <div className="mt-0.5 text-[11.5px] text-fg-muted">= {packUnitBreakdown(l.quantity, l.packUnits)}</div>}
                          {pendingStock && !l.addedByRule && <LineAvailability orderId={o.id} entries={entriesFor(availability.data, l.productId)} boostedAt={availability.data?.boostedAt ?? null} />}
                          {!pendingStock && !l.addedByRule && <LineShipment o={o} lineId={l.id} />}
                          {!!l.batches?.length && (
                            <div className="mt-0.5 text-[11.5px] text-fg-muted">
                              {t("Vorgabe:")} {l.batches.map((b) => `Charge ${b.batch}${b.bestBefore ? ` · MHD ${new Date(b.bestBefore).toLocaleDateString(intlLocale())}` : ""} (${b.quantity})`).join("; ")}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{l.quantity}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-fg-muted hidden sm:table-cell">{l.unitPriceMinor != null ? fmtMoney(l.unitPriceMinor, o.currency) : "—"}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{l.unitPriceMinor != null ? fmtMoney(l.unitPriceMinor * l.quantity, o.currency) : "—"}</td>
                  </tr>
                ))}
              </tbody>
              {o.totalMinor != null && (
                <tfoot className="border-t border-border">
                  <tr>
                    <td colSpan={2} className="px-3 py-2 text-[12px] text-fg-muted sm:hidden">{t("Bestellwert inkl. Versand")}</td>
                    <td colSpan={3} className="px-3 py-2 text-[12px] text-fg-muted hidden sm:table-cell text-right">{t("Bestellwert inkl. Versand")}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtMoney(o.totalMinor, o.currency)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Section>

        {/* Empfänger & Versand: zwei gleich gebaute Karten */}
        <div className="grid sm:grid-cols-2 gap-3">
          <InfoCard
            title={t("Empfänger")}
            icon={<MapPin className="size-4" />}
            action={
              <button onClick={() => copy(addressText(a), "Adresse")} className="size-7 grid place-items-center rounded-md text-fg-subtle hover:text-fg hover:bg-surface-2" aria-label={t("Adresse kopieren")}>
                <Copy className="size-3.5" />
              </button>
            }
          >
            {o.addressCorrection && (
              <div className="mb-2.5 rounded-lg border border-info/30 bg-info-soft/60 px-2.5 py-2 text-[12px]">
                <p className="flex items-center gap-1.5 font-semibold text-info"><MapPinCheck className="size-3.5" /> {t("Adresse korrigiert")} <span className="font-normal text-fg-muted">· {t("ursprünglich → jetzt")}</span></p>
                {o.addressCorrection.changes.length > 0 ? (
                  <ul className="mt-1 space-y-0.5 text-fg-muted">
                    {o.addressCorrection.changes.map((c) => (
                      <li key={c.field}>{t(FIELD_LABEL[c.field] ?? c.field)}: <span className="line-through">{c.from || "—"}</span> → <span className="text-fg font-medium">{c.to || "—"}</span></li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-0.5 text-fg-muted">{t("Ursprüngliche Adresse wurde bestätigt.")}</p>
                )}
                {/* jede einzelne Korrektur: wann, durch wen, was */}
                {o.history.some((h) => (ADDRESS_AUDIT_ACTIONS as readonly string[]).includes(h.action)) && (
                  <ol className="mt-2 pt-2 border-t border-info/20 space-y-1 text-[11.5px] text-fg-muted">
                    {o.history.filter((h) => (ADDRESS_AUDIT_ACTIONS as readonly string[]).includes(h.action)).map((h) => (
                      <li key={h.id}><span className="tabular-nums text-fg-subtle">{fmtDateTime(h.createdAt)}</span>{h.actorLabel ? <span className="text-fg-subtle"> · {h.actorLabel}</span> : null}<span className="block text-fg">{serverText(h.message)}</span></li>
                    ))}
                  </ol>
                )}
              </div>
            )}
            <address className="not-italic text-[13px] leading-5">
              <span className="font-medium">{[a.firstName, a.lastName].filter(Boolean).join(" ")}</span>
              {a.company && <span className="block text-fg-muted">{a.company}</span>}
              {isPickupPoint(a.street) && (
                <span className="flex flex-wrap items-center gap-1.5 my-1">
                  <Badge tone="info">{/postfiliale/i.test(a.street) ? t("Postfiliale") : t("Packstation")}</Badge>
                  {a.addition && <span className="text-[12px] text-fg-muted">{t("Postnummer")} {a.addition}</span>}
                </span>
              )}
              <span className="block">{[a.street, a.houseNumber].filter(Boolean).join(" ")}</span>
              {a.addition && !isPickupPoint(a.street) && <span className="block">{a.addition}</span>}
              <span className="block">{a.zip} {a.city}</span>
              <span className="flex items-center gap-1.5 text-fg-muted"><Flag country={a.country} title={false} />{countryName(a.country)}{/* Export = Zoll, ggf. Ausfuhrbegleitdokument */}{a.country && !EU_COUNTRIES.has(a.country.toUpperCase()) && <Badge tone="warning" icon={<Globe2 className="size-3" />}>{t("Export")}</Badge>}</span>
            </address>
            {(a.email || a.phone) && (
              <div className="mt-2 pt-2 border-t border-border space-y-1 text-[12.5px] text-fg-muted">
                {a.email && <a href={`mailto:${a.email}`} className="flex items-center gap-1.5 hover:text-fg truncate"><Mail className="size-3.5 shrink-0" />{a.email}</a>}
                {a.phone && <a href={`tel:${a.phone}`} className="flex items-center gap-1.5 hover:text-fg"><Phone className="size-3.5 shrink-0" />{a.phone}</a>}
              </div>
            )}
          </InfoCard>

          <InfoCard title={t("Versand")} icon={<Truck className="size-4" />}>
            <dl className="grid grid-cols-[88px_minmax(0,1fr)] gap-x-3 gap-y-2.5 text-[13px]">
              {o.warehouse && (
                <>
                  <dt className="text-[12px] text-fg-subtle pt-px">{t("Lager")}</dt>
                  <dd>
                    {o.warehouse.code ? <WarehouseBadge code={o.warehouse.code} name={o.warehouse.name} /> : <span className="font-medium">{o.warehouse.name}</span>}
                    {o.warehouse.reason && <span className="block text-[12px] text-fg-muted">{serverText(o.warehouse.reason)}</span>}
                  </dd>
                </>
              )}
              <dt className="text-[12px] text-fg-subtle pt-px">{t("Versandart")}</dt>
              <dd>
                {/* maßgeblich ist, womit das Lager versendet – die Wahl im Shop nur als Herkunft */}
                <span className="font-medium">{methodName(o.shippingMethodId)}</span>
                {me.user.isStaff && internalMethodName(o.shippingMethodId) && <span className="block text-[11.5px] text-fg-subtle">{t("im Lager: {name}", { name: internalMethodName(o.shippingMethodId)! })}</span>}
                <span className="block text-[12px] text-fg-muted">
                  {!o.channelShippingMethod
                    ? o.channelKind === "manual" ? t("im Auftrag festgelegt") : t("im Shop keine gewählt")
                    : o.channelShippingMethod.trim().toLowerCase() === methodName(o.shippingMethodId).trim().toLowerCase()
                      ? t("wie {v} gewählt", { v: o.channelKind === "manual" ? t("im Auftrag") : t("im Shop") })
                      : `${o.channelKind === "manual" ? "im Auftrag" : "im Shop"}: ${o.channelShippingMethod}`}
                </span>
              </dd>
              {o.priority !== 0 && (
                <>
                  <dt className="text-[12px] text-fg-subtle pt-px">{t("Priorität")}</dt>
                  <dd>{o.priority > 0 ? t("+{priority} (bevorzugt)", { priority: o.priority }) : o.priority}</dd>
                </>
              )}
              {o.links.backendRef && (
                <>
                  <dt className="text-[12px] text-fg-subtle pt-px">{t("Lager-Nr.")}</dt>
                  <dd className="flex items-center gap-1 min-w-0">
                    <span className="tabular-nums truncate">{o.links.backendRef}</span>
                    <button onClick={() => copy(o.links.backendRef!, "Lager-Nummer")} className="size-6 grid place-items-center rounded text-fg-subtle hover:text-fg shrink-0" aria-label={t("Lager-Nummer kopieren")}><Copy className="size-3" /></button>
                  </dd>
                </>
              )}
            </dl>
          </InfoCard>
        </div>

        {(o.note || o.tags.length > 0 || (o.channelFields?.length ?? 0) > 0) && (
          <Section title={t("Hinweise")} icon={<StickyNote className="size-4" />}>
            {o.note && <p className="text-[13px] rounded-lg bg-surface-2 px-3 py-2 whitespace-pre-line">„{o.note}"</p>}
            {o.tags.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">{o.tags.map((t) => <Badge key={t}>{t}</Badge>)}</div>}
            {(o.channelFields?.length ?? 0) > 0 && (
              <dl className="mt-2.5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[13px]">
                {o.channelFields!.map((f) => (
                  <div key={f.label} className="contents">
                    <dt className="text-fg-muted">{t(f.label)}</dt>
                    <dd className="min-w-0 break-words">{f.value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </Section>
        )}

        {/* Versandentscheidung – nur Logiful */}
        {o.shippingDecision && (
          <Section title={t("Versandentscheidung · nur Logiful")}>
            <div className="rounded-xl border border-ink/20 bg-ink/[0.03] p-3 text-[12.5px] space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium" title={o.shippingDecision.methodId ?? undefined}>{o.shippingDecision.methodId ? methodName(o.shippingDecision.methodId) : t("keine")}</span>
                {o.shippingDecision.costMinor != null && <span className="tabular-nums text-fg-muted">{fmtMoney(o.shippingDecision.costMinor, "EUR")} {t("Einkauf")}</span>}
                {o.customerShippingMethodId && o.customerShippingMethodId !== o.shippingDecision.methodId && <Badge>{t("Kunde sieht")}: {methodName(o.customerShippingMethodId)}</Badge>}
                {(o.optimizations ?? []).map((l) => <Badge key={l} tone="info">{t("optimization:")} {l}</Badge>)}
              </div>
              {o.shippingDecision.packages.length > 0 && (
                <ul className="space-y-0.5 text-fg-muted">
                  {o.shippingDecision.packages.map((p, i) => <li key={i}>📦 {p.packaging} · {Math.round(p.outer.l)}×{Math.round(p.outer.w)}×{Math.round(p.outer.h)} {t("cm ·")} {p.weightKg.toLocaleString(intlLocale())} {t("kg ·")} {Math.round(p.fill * 100)} {t("% gefüllt ·")} {p.skus.length} {t("Teile")}</li>)}
                </ul>
              )}
              <ul className="space-y-0.5">
                {o.shippingDecision.explanation.map((e, i) => <li key={i} className={cx(e.visibility === "internal" && "text-fg-muted")}>{e.visibility === "internal" ? "🔒 " : ""}{t(e.text)}</li>)}
              </ul>
              {o.shippingDecision.alternatives.length > 0 && (
                <details>
                  <summary className="cursor-pointer text-fg-muted">{t("Alternativen (")}{o.shippingDecision.alternatives.length})</summary>
                  <ul className="mt-1 space-y-0.5 tabular-nums">
                    {o.shippingDecision.alternatives.map((a) => <li key={a.methodId} title={a.methodId}>{methodName(a.methodId)}: {a.reason ? <span className="text-fg-subtle">{serverText(a.reason)}</span> : a.costMinor != null ? fmtMoney(a.costMinor, "EUR") : t("ohne Preis")}</li>)}
                  </ul>
                </details>
              )}
            </div>
          </Section>
        )}

        {/* Verlauf */}
        <Section title={t("Verlauf")}>
          <ol className="relative border-l border-border ml-2 space-y-4">
            {[...o.history].reverse().map((h) => (
              <li key={h.id} className="ml-4">
                <span className={cx("absolute -left-[5px] mt-1.5 size-2.5 rounded-full border-2 bg-surface", h.action.includes("shipped") ? "border-success" : h.action.includes("hold") ? "border-warning" : h.action.includes("rejected") ? "border-danger" : "border-fg-subtle")} />
                <p className="text-[13px]">{serverText(h.message ?? h.action)}</p>
                <p className="text-[11.5px] text-fg-subtle">{fmtDateTime(h.createdAt)} · {h.actorLabel ?? ACTOR[h.actorType] ?? h.actorType}</p>
              </li>
            ))}
          </ol>
        </Section>
      </div>
    </>
  );
}

/** Karte mit Kopfzeile (Empfänger, Versand) */
function InfoCard({ title, icon, action, children }: { title: string; icon?: ReactNode; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-border p-4">
      <div className="mb-2.5 flex items-center gap-1.5">
        <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide flex items-center gap-1.5">{icon}{t(title)}</h3>
        <span className="ml-auto">{action}</span>
      </div>
      {children}
    </section>
  );
}

function LineThumb({ url }: { url: string | null }) {
  return url ? (
    <img src={url} alt="" loading="lazy" className="size-10 shrink-0 rounded-lg border border-border object-cover bg-surface-2" />
  ) : (
    <span className="size-10 shrink-0 rounded-lg border border-border bg-surface-2 grid place-items-center text-fg-subtle"><Package className="size-4" /></span>
  );
}

const FIELD_LABEL: Record<string, string> = { company: msg("Firma"), firstName: msg("Vorname"), lastName: msg("Nachname"), street: msg("Straße"), houseNumber: msg("Hausnummer"), addition: msg("Zusatz"), zip: msg("PLZ"), city: msg("Ort"), state: msg("Region"), country: msg("Land") };

/** Packstation / Postfiliale / Paketshop */
const isPickupPoint = (street: string) => /packstation|postfiliale|paketshop|paketbox/i.test(street);

const addressText = (a: OrderDetail["shippingAddress"]) =>
  [a.company, [a.firstName, a.lastName].filter(Boolean).join(" "), [a.street, a.houseNumber].filter(Boolean).join(" "), a.addition, `${a.zip} ${a.city}`, countryName(a.country)].filter(Boolean).join("\n");

function Section({ title, icon, children, aside }: { title: string; icon?: ReactNode; children: ReactNode; aside?: ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-[12px] font-semibold text-fg-muted uppercase tracking-wide flex items-center gap-1.5">{icon}{t(title)}{aside}</h3>
      {children}
    </section>
  );
}

/** Bestätigung fürs Erzwingen – zeigt, was übergangen wird */
const PART_STATUS: Record<string, string> = { received: "eingegangen", ready: "Stornofenster", on_hold: "wartet", failed: "Fehler", submitted: "im Lager", in_progress: "in Bearbeitung", partially_shipped: "teilweise versendet", shipped: "versendet", cancelled: "storniert" };

/** „Teilliefern“: Vorschau, was jetzt rausgeht und was als Restlieferung wartet */
function PartialDialog({ o, onClose }: { o: OrderDetail; onClose: () => void }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const preview = useQuery({ queryKey: ["partial-preview", tenantId, o.id], queryFn: () => api.orders.partialPreview(tenantId, o.id) });
  const go = useMutation({
    mutationFn: () => api.orders.partialDeliver(tenantId, o.id),
    onSuccess: (r) => {
      toast.success(r.result === "complete" ? "Alles lieferbar – geht komplett raus" : `Teillieferung angelegt – Rest als ${r.restOrderNumber}`, { description: msg("Der lieferbare Teil wird jetzt ans Lager übergeben, der Rest folgt automatisch, sobald Bestand da ist.") });
      void qc.invalidateQueries({ queryKey: ["order", tenantId, o.id] });
      void qc.invalidateQueries({ queryKey: ["orders-search", tenantId] });
      onClose();
    },
    onError: (e) => toast.error("Nicht möglich", e instanceof ApiError ? { description: (e.body as { message?: string })?.message } : undefined),
  });
  const p = preview.data;
  const List = ({ items, tone }: { items: { sku: string; name: string; quantity: number }[]; tone: "ship" | "rest" }) => (
    <ul className="mt-1.5 rounded-xl border border-border divide-y divide-border">
      {items.map((l, i) => (
        <li key={`${l.sku}-${i}`} className="flex items-center gap-3 px-3 py-2 text-[13px]">
          <span className={cx("tabular-nums font-semibold w-8", tone === "ship" ? "text-success" : "text-fg-muted")}>{l.quantity}×</span>
          <span className="min-w-0 flex-1 truncate">{l.name}</span>
          <span className="font-mono text-[11.5px] text-fg-muted">{l.sku}</span>
        </li>
      ))}
    </ul>
  );
  return (
    <Dialog.Root open onOpenChange={(v) => !v && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-[70] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(520px,calc(100vw-24px))] max-h-[calc(100vh-24px)] overflow-y-auto rounded-2xl border border-border bg-surface p-6 shadow-2xl animate-pop-in">
          <Dialog.Title className="text-[17px] font-semibold flex items-center gap-2"><Split className="size-4 text-fg-subtle" /> {t("Auftrag")} {o.orderNumber} {t("teilliefern")}</Dialog.Title>
          <Dialog.Description className="text-[13px] text-fg-muted mt-1">{t("Was lieferbar ist, geht jetzt ans Lager. Der Rest wird eine verknüpfte Restlieferung und folgt automatisch, sobald Bestand da ist – der Kunde bekommt je Paket eine Versandbestätigung.")}</Dialog.Description>
          {preview.isLoading ? (
            <div className="mt-4 space-y-2"><Skeleton className="h-9 w-full" /><Skeleton className="h-9 w-full" /></div>
          ) : p ? (
            <>
              <div className="mt-4">
                <p className="text-[12px] font-semibold uppercase tracking-wide text-fg-muted">{t("Geht jetzt raus")}</p>
                {p.ship.length ? <List items={p.ship} tone="ship" /> : <p className="mt-1.5 text-[13px] text-warning">{t("Derzeit ist keine Position lieferbar.")}</p>}
              </div>
              {p.rest.length > 0 && (
                <div className="mt-4">
                  <p className="text-[12px] font-semibold uppercase tracking-wide text-fg-muted">{t("Restlieferung")} {o.orderNumber}{t("-T2 · folgt bei Bestand")}</p>
                  <List items={p.rest} tone="rest" />
                </div>
              )}
            </>
          ) : null}
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>{t("Abbrechen")}</Button>
            <Button variant="primary" disabled={!p?.ship.length} loading={go.isPending} onClick={() => go.mutate()}>{p && !p.rest.length ? t("Komplett übertragen") : t("Teillieferung starten")}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Retouren dieses Auftrags aus der Retouren-Bearbeitung */

function OrderReturns({ orderId, orderNumber, canTest }: { orderId: string; orderNumber: string; canTest: boolean }) {
  const { tenantId } = useTenant();
  const q = useQuery({
    queryKey: ["order-returns", tenantId, orderId],
    queryFn: () => api.returns.ofOrder(tenantId, orderId),
    staleTime: 60_000,
    // laufende Shopify-Meldung: nachladen, bis das Ergebnis da ist
    refetchInterval: (query) => (query.state.data?.some((r) => r.shopify?.state === "pending" || (r.shopify === null && !r.undeliverable && Date.now() - new Date(r.processedAt ?? 0).getTime() < 120_000)) ? 3000 : false),
  });
  if (!q.data?.length) return null;
  return (
    <div className="mt-3 rounded-lg border border-border px-3 py-2.5 text-[12.5px] space-y-2">
      {q.data.map((r) => (
        <div key={r.id}>
          <p className="flex flex-wrap items-center gap-2">
            <RotateCcw className="size-3.5 text-fg-subtle" />
            <span className="font-medium">{t("Retoure")} {r.processedAt ? t("vom {v}", { v: fmtDateTime(r.processedAt) }) : ""}</span>
            <Badge tone={r.status === "shop_sync_failed" ? "danger" : r.status === "shop_synced" ? "success" : "neutral"}>{t(r.statusLabel)}</Badge>
            {r.undeliverable && <Badge tone="warning">{t("Rückläufer")}</Badge>}
            <span className="ml-auto flex items-center gap-3">
              <ReturnsPortalButton variant="link" q={orderNumber} />
              <Link to={`/returns?open=${r.id}`} className="underline underline-offset-2">{t("Details")}</Link>
            </span>
          </p>
          <ul className="mt-1 text-fg-muted">
            {r.lines.map((l, i) => <li key={i}>{l.quantity}× {l.name ?? l.sku}{l.condition ? ` · ${CONDITION_LABEL[l.condition] ?? l.condition}` : ""}{l.undeliverableReason ? ` · ${serverText(l.undeliverableReason)}` : ""}{l.conditionReasons?.length ? ` · ${l.conditionReasons.join(", ")}` : ""}</li>)}
          </ul>
          {(r.shopify || canTest) && (
            <div className="mt-1.5 text-[12.5px]">
              <ShopifyReturnStatus caseId={r.id} shopify={r.shopify} canPush={canTest} invalidate={[["order-returns", tenantId, orderId], ["return", tenantId, r.id], ["returns", tenantId]]} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ForceDialog({ o, open, onOpenChange }: { o: OrderDetail; open: boolean; onOpenChange: (v: boolean) => void }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const notInWarehouse = o.lines.filter((l) => l.blocked);
  const force = useMutation({
    mutationFn: () => api.orders.force(tenantId, o.id),
    onSuccess: () => {
      toast.success(`Auftrag ${o.orderNumber} wird ans Lager übertragen`, { description: msg("Das Ergebnis erscheint in wenigen Sekunden im Verlauf.") });
      onOpenChange(false);
      void qc.invalidateQueries({ queryKey: ["order", tenantId, o.id] });
      void qc.invalidateQueries({ queryKey: ["orders-search", tenantId] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Erzwingen fehlgeschlagen") : "Erzwingen fehlgeschlagen"),
  });
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-[70] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(480px,calc(100vw-32px))] rounded-2xl border border-border bg-surface p-6 shadow-2xl animate-pop-in">
          <Dialog.Title className="text-[17px] font-semibold flex items-center gap-2"><Send className="size-4 text-fg-subtle" /> {t("Übertragung erzwingen?")}</Dialog.Title>
          <Dialog.Description className="mt-1 text-[13px] text-fg-muted">{t("Auftrag")} {o.orderNumber} {t("geht sofort ans Lager – ohne die Prüfungen abzuwarten.")}</Dialog.Description>
          {o.holdReasons.length > 0 && (
            <div className="mt-4 rounded-xl border border-warning/30 bg-warning-soft/60 p-3 text-[13px]">
              <p className="font-medium">{t("Diese Gründe werden übergangen:")}</p>
              <ul className="mt-1 space-y-0.5 text-fg-muted">{o.holdReasons.map((r) => <li key={r}>· {serverText(r)}</li>)}</ul>
            </div>
          )}
          {notInWarehouse.length > 0 && (
            <p className="mt-3 text-[12.5px] text-fg-muted flex items-start gap-1.5"><TriangleAlert className="size-3.5 mt-0.5 text-warning shrink-0" /> {tn(notInWarehouse.length, "{skus} wird dabei mit den vorhandenen Stammdaten im Lager angelegt. Lehnt das Lager ab, zeigt der Auftrag den Grund.", "{skus} werden dabei mit den vorhandenen Stammdaten im Lager angelegt. Lehnt das Lager ab, zeigt der Auftrag den Grund.", { skus: notInWarehouse.map((l) => l.sku).join(", ") })}</p>
          )}
          <p className="mt-3 text-[12px] text-fg-subtle">{t("Im Verlauf wird festgehalten, wer die Übertragung erzwungen hat.")}</p>
          <div className="mt-6 flex justify-end gap-2">
            <Dialog.Close asChild><Button variant="ghost">{t("Abbrechen")}</Button></Dialog.Close>
            <Button variant="primary" loading={force.isPending} onClick={() => force.mutate()}>{t("Jetzt übertragen")}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const fmtKg = (n: number) => `${n.toLocaleString(intlLocale(), { maximumFractionDigits: 2 })} kg`;
const fmtDay = (ymd: string) => ymd.split("-").reverse().join(".");

/** Track & Trace: je Paket der aktuelle Stand (immer sichtbar) */
function DeliveryLines({ tracking }: { tracking: PackageTracking[] }) {
  return (
    <ul className="px-3 pb-2 space-y-1">
      {tracking.map((pkg) => {
        const v = pkg.status ? DELIVERY_VIEW[pkg.status] : null;
        return (
          <li key={pkg.trackingNumber} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px]">
            {v ? <Badge tone={v.tone}>{t(v.label)}</Badge> : <Badge>{t("Noch keine Daten von DHL")}</Badge>}
            {tracking.length > 1 && <span className="font-mono text-[11.5px] text-fg-subtle">…{pkg.trackingNumber.slice(-6)}</span>}
            {pkg.statusText && <span className="min-w-0 text-fg-muted">{pkg.statusText}</span>}
            {(pkg.deliveredAt ?? pkg.lastEventAt) && <span className="text-fg-subtle">· {fmtRelative(pkg.deliveredAt ?? pkg.lastEventAt)}</span>}
            {!pkg.status && pkg.lastError && <span className="text-fg-subtle">· {pkg.lastError}</span>}
          </li>
        );
      })}
    </ul>
  );
}

/** Sendungsverlauf von DHL (neueste zuerst) */
function DeliveryTimeline({ tracking }: { tracking: PackageTracking[] }) {
  return (
    <div>
      <p className="text-[11.5px] font-semibold uppercase tracking-wide text-fg-muted">{t("Sendungsverlauf")}</p>
      {tracking.filter((pkg) => pkg.events.length).map((pkg) => (
        <div key={pkg.trackingNumber} className="mt-1.5">
          {tracking.length > 1 && <p className="font-mono text-[11.5px] text-fg-subtle">{pkg.trackingNumber}</p>}
          <ol className="mt-1 border-l border-border ml-1.5 space-y-2">
            {pkg.events.map((e, i) => (
              <li key={i} className="relative pl-4 text-[12.5px]">
                <span className={cx("absolute -left-[5px] top-1.5 size-2.5 rounded-full border-2 border-surface", i === 0 ? "bg-coral" : "bg-border-strong")} />
                <span className={cx("block", i === 0 ? "text-fg" : "text-fg-muted")}>{t(e.text)}</span>
                <span className="block text-[11.5px] text-fg-subtle">{[e.at ? fmtDateTime(e.at) : null, e.location].filter(Boolean).join(" · ")}</span>
              </li>
            ))}
          </ol>
          {pkg.lastCheckedAt && <p className="mt-1.5 text-[11px] text-fg-subtle">{t("zuletzt bei DHL abgefragt")} {fmtRelative(pkg.lastCheckedAt)}</p>}
        </div>
      ))}
    </div>
  );
}

/** abgelehnte Versandmeldung sofort erneut senden (z. B. nach Korrektur im Kanal) */
function RetryReportButton({ orderId }: { orderId: string }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const retry = useMutation({
    mutationFn: () => api.orders.retryChannelReport(tenantId, orderId),
    onSuccess: () => { toast.success("Versandmeldung wird erneut gesendet", { description: msg("Das Ergebnis erscheint gleich hier.") }); setTimeout(() => void qc.invalidateQueries({ queryKey: ["order", tenantId, orderId] }), 4000); },
    onError: (e) => toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Nicht möglich") : "Nicht möglich"),
  });
  return <Button size="sm" className="mt-2" icon={<RefreshCw className="size-3.5" />} loading={retry.isPending} onClick={() => retry.mutate()}>{t("Jetzt erneut senden")}</Button>;
}

/** Sendung: Kopf mit Tracking; ein Klick zeigt Inhalt (mit Charge/MHD) und Pakete (Gewicht, Maße) */
function ShipmentCard({ s, o, copy }: { s: OrderDetail["shipments"][number]; o: OrderDetail; copy: (text: string, label: string) => void }) {
  const [open, setOpen] = useState(false);
  const nameOf = new Map(o.lines.map((l) => [l.sku, l.name]));
  const packages = s.packages ?? [];
  const count = Math.max(packages.length, s.trackingNumbers.length, 1);
  const units = s.lines.reduce((n, l) => n + l.quantity, 0);
  const noShopReport = ["manual", "b2b", "csv", "fba"].includes(o.channelKind ?? "");
  return (
    <div className="rounded-xl border border-border">
      <button type="button" onClick={() => setOpen((x) => !x)} aria-expanded={open} className="w-full text-left p-3 pb-2 flex items-center gap-2 rounded-t-xl hover:bg-surface-2/40">
        <PackageCheck className="size-4 text-success" />
        <span className="font-medium text-[13px]">{s.carrier}</span>
        <span className="text-[12px] text-fg-muted">· {fmtDateTime(s.shippedAt)}</span>
        <span className="text-[12px] text-fg-muted">· {count} {count === 1 ? t("Paket") : t("Pakete")}{units ? t(", {units} Stk.", { units }) : ""}</span>
        <span className="ml-auto flex items-center gap-2">
          {noShopReport ? null : s.reportedToShop ? <span className="inline-flex items-center gap-1 text-[12px] text-success"><CheckCircle2 className="size-3.5" /> {t("an")} {shopLabel(o.channelKind)} {t("gemeldet")}</span> : <Badge tone="warning">{t("Meldung an")} {shopLabel(o.channelKind)} {t("ausstehend")}</Badge>}
          <ChevronDown className={cx("size-4 text-fg-subtle transition-transform", open && "rotate-180")} />
        </span>
      </button>
      {!s.reportedToShop && s.channelError && (
        <div className="mx-3 mb-2 flex gap-2 rounded-lg border border-danger/25 bg-danger-soft/60 px-3 py-2 text-[12.5px]">
          <AlertTriangle className="size-4 shrink-0 text-danger mt-0.5" />
          <span>
            <span className="font-medium text-danger">{shopLabel(o.channelKind)} {t("hat die Versandmeldung abgelehnt:")}</span> {s.channelError.replace(/^Odoo:s*/, "")}
            <span className="block text-fg-muted text-[11.5px] mt-0.5">{t("Connect versucht es bei jedem Abgleich erneut – nach der Korrektur geht die Meldung automatisch raus.")}</span>
            <RetryReportButton orderId={o.id} />
          </span>
        </div>
      )}
      {s.tracking && s.tracking.length > 0 && <DeliveryLines tracking={s.tracking} />}
      <div className="px-3 pb-3 flex flex-wrap gap-2">
        {s.trackingNumbers.map((n) => (
          <span key={n} className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1 rounded-lg bg-surface-2 border border-border font-mono text-[12px]">
            {n}
            <button onClick={() => copy(n, "Trackingnummer")} className="size-5 grid place-items-center rounded text-fg-subtle hover:text-fg" aria-label={t("Trackingnummer kopieren")}><Copy className="size-3" /></button>
            <a href={trackingUrl(s.carrier, n)} target="_blank" rel="noreferrer" className="size-5 grid place-items-center rounded text-fg-subtle hover:text-fg" aria-label={t("Sendung verfolgen")}><ExternalLink className="size-3" /></a>
          </span>
        ))}
      </div>
      {open && (
        <div className="border-t border-border px-3 py-2.5 space-y-3 animate-fade-in">
          {s.tracking?.some((t) => t.events.length) && <DeliveryTimeline tracking={s.tracking} />}
          <div>
            <p className="text-[11.5px] font-semibold uppercase tracking-wide text-fg-muted">{count === 1 ? t("Inhalt des Pakets") : t("Inhalt der Sendung ({count} Pakete)", { count })}</p>
            {s.lines.length ? (
              <ul className="mt-1.5 divide-y divide-border text-[13px]">
                {s.lines.map((l, i) => (
                  <li key={`${l.sku}-${l.batch ?? ""}-${i}`} className="py-1.5 flex items-baseline gap-3">
                    <span className="tabular-nums font-semibold w-10 text-right">{l.quantity}×</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{nameOf.get(l.sku) ?? l.sku}</span>
                      <span className="block text-[12px] text-fg-muted font-mono">{l.sku}{l.batch ? t(" · Charge {batch}", { batch: l.batch }) : ""}{l.bestBefore ? t(" · MHD {v}", { v: fmtDay(l.bestBefore) }) : ""}</span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-[12.5px] text-fg-muted">{t("Das Lager hat zu dieser Sendung keinen Inhalt gemeldet.")}</p>
            )}
          </div>
          {packages.length > 1 && (
            <div>
              <p className="text-[11.5px] font-semibold uppercase tracking-wide text-fg-muted">{t("Pakete")}</p>
              <ul className="mt-1.5 space-y-1 text-[12.5px]">
                {packages.map((p, i) => (
                  <li key={i} className="flex flex-wrap gap-x-3 text-fg-muted">
                    <span className="text-fg font-medium">{t("Paket")} {i + 1}</span>
                    {p.trackingNumber && <span className="font-mono">{p.trackingNumber}</span>}
                    {p.weight != null && <span>{fmtKg(p.weight)}</span>}
                    {p.dimensions && <span>{p.dimensions.length} × {p.dimensions.width} × {p.dimensions.height} {t("cm")}</span>}
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 text-[11.5px] text-fg-subtle">{t("Das Lager meldet den Inhalt je Sendung, nicht je Paket – welcher Artikel in welchem Paket liegt, ist daher nicht bekannt.")}</p>
            </div>
          )}
          {packages.length === 1 && (packages[0]!.weight != null || packages[0]!.dimensions) && (
            <p className="text-[12.5px] text-fg-muted">
              {packages[0]!.weight != null ? fmtKg(packages[0]!.weight) : ""}
              {packages[0]!.weight != null && packages[0]!.dimensions ? " · " : ""}
              {packages[0]!.dimensions ? t("{length} × {width} × {height} cm", { length: packages[0]!.dimensions.length, width: packages[0]!.dimensions.width, height: packages[0]!.dimensions.height }) : ""}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** Freigabetermin setzen/ändern/aufheben – im Lager live geprüft (bis zur Kommissionierung) */
function DeferDialog({ o, tenantId, onClose }: { o: OrderDetail; tenantId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const ymd = (d: Date) => new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Berlin" }).format(d);
  const min = ymd(new Date(Date.now() + 86_400_000));
  const max = ymd(new Date(Date.now() + 90 * 86_400_000));
  const [date, setDate] = useState(o.deferredUntil ? ymd(new Date(o.deferredUntil)) : "");
  const done = () => {
    void qc.invalidateQueries({ queryKey: ["order", tenantId, o.id] });
    void qc.invalidateQueries({ queryKey: ["orders-search", tenantId] });
    onClose();
  };
  const err = (e: unknown) => toast.error(t("Freigabetermin nicht gesetzt"), { description: e instanceof ApiError ? ((e.body as { message?: string })?.message ?? t("Bitte erneut versuchen.")) : t("Bitte erneut versuchen."), duration: 10_000 });
  const save = useMutation({
    mutationFn: (until: string | null) => api.orders.defer(tenantId, o.id, until),
    onSuccess: (r, until) => {
      if (until) toast.success(t("Zurückgestellt bis {d}", { d: new Date(`${until}T12:00:00`).toLocaleDateString(intlLocale()) }), { description: r.inWarehouse ? t("Im Lager storniert (live geprüft) – wird am Termin neu übertragen.") : t("Geht am Termin automatisch ans Lager.") });
      else toast.success(t("Freigabetermin aufgehoben"), { description: t("Der Auftrag läuft jetzt weiter.") });
      done();
    },
    onError: (e) => {
      err(e);
      void qc.invalidateQueries({ queryKey: ["order", tenantId, o.id] });
    },
  });
  const valid = !!date && date >= min && date <= max;
  return (
    <Dialog.Root open onOpenChange={(v) => !v && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[61] w-[min(440px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-surface p-5 shadow-xl">
          <Dialog.Title className="text-[16px] font-semibold">{t("Freigabetermin für {n}", { n: o.orderNumber })}</Dialog.Title>
          <Dialog.Description className="mt-1 text-[13px] text-fg-muted">
            {t("Der Auftrag wird erst an diesem Tag ans Lager übergeben – z. B. wenn der Endkunde im Urlaub ist. Die Ware bleibt so lange für ihn reserviert.")}
          </Dialog.Description>
          {!o.editable && (
            <p className="mt-3 rounded-lg bg-warning-soft px-3 py-2 text-[12.5px]">{t("Der Auftrag ist schon im Lager. Das Portal prüft live und storniert ihn dort, sofern die Kommissionierung noch nicht begonnen hat – am Termin wird er neu übertragen.")}</p>
          )}
          <label className="mt-4 block">
            <span className="text-[12.5px] font-medium text-fg-muted">{t("Versenden ab")}</span>
            <input type="date" min={min} max={max} value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-border bg-surface px-2 text-[14px]" />
          </label>
          <p className="mt-1 text-[12px] text-fg-subtle">{t("Frühestens morgen, höchstens in 90 Tagen. Übergabe am Morgen des Tages.")}</p>
          <div className="mt-5 flex flex-wrap justify-between gap-2">
            {o.deferredUntil ? <Button variant="ghost" disabled={save.isPending} onClick={() => save.mutate(null)}>{t("Termin aufheben – sofort weiter")}</Button> : <span />}
            <span className="flex gap-2">
              <Button variant="ghost" onClick={onClose}>{t("Abbrechen")}</Button>
              <Button variant="primary" disabled={!valid} loading={save.isPending} onClick={() => save.mutate(date)}>{t("Speichern")}</Button>
            </span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
